// Suggests French cities on the birth city field, arrondissements included (Paris 3e, Lyon 7e…), from the
// Géoplateforme (IGN's address API); picking one fills the birth postcode too. Both fields stay free text
// for a birth abroad. A list of our own rather than a <datalist>: the browser would filter the API's
// answers on the exact typed text, hiding « Saint-Étienne » from « saint etienne ».

import { Controller } from "@hotwired/stimulus"

const API = "https://data.geopf.fr/geocodage/search"

export default class extends Controller {
  static values = { zipcode: String }

  get city() { return this.element }
  get zipcode() { return document.getElementById(this.zipcodeValue) }

  connect() {
    this.menu = document.createElement("ul")
    this.menu.id = `${this.city.id}_suggestions`
    this.menu.className = "dropdown-menu w-100"
    this.menu.setAttribute("role", "listbox")
    this.menu.addEventListener("mousedown", (event) => event.preventDefault())
    this.city.parentElement.classList.add("position-relative")
    this.city.insertAdjacentElement("afterend", this.menu)
    Object.entries({ role: "combobox", "aria-autocomplete": "list", "aria-controls": this.menu.id, "aria-expanded": "false" })
      .forEach(([name, value]) => this.city.setAttribute(name, value))

    this.onInput = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.search(), 250) }
    this.onKeydown = this.keydown.bind(this)
    this.onBlur = () => this.close()
    this.city.addEventListener("input", this.onInput)
    this.city.addEventListener("keydown", this.onKeydown)
    this.city.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.menu?.remove()
    this.city?.removeEventListener("input", this.onInput)
    this.city?.removeEventListener("keydown", this.onKeydown)
    this.city?.removeEventListener("blur", this.onBlur)
  }

  async search() {
    const query = this.city.value.trim()
    this.request?.abort()
    if (query.length < 2) return this.close()

    this.request = new AbortController()
    try {
      const params = new URLSearchParams({ q: query, type: "municipality", autocomplete: 1, limit: 8 })
      const response = await fetch(`${API}?${params}`, { signal: this.request.signal })
      this.render((await response.json()).features?.map((feature) => feature.properties) || [])
    } catch {
      // Aborted by a newer keystroke, or the API is down: the field stays free text.
    }
  }

  render(cities) {
    this.cities = cities
    this.active = -1
    this.menu.replaceChildren(...cities.map((city, index) => {
      const item = document.createElement("li")
      item.id = `${this.menu.id}_${index}`
      item.className = "dropdown-item pointer d-flex justify-content-between gap-3"
      item.setAttribute("role", "option")
      const department = city.context?.split(", ")[1]
      item.innerHTML = `<span></span><span class="text-body-secondary small"></span>`
      item.firstChild.textContent = city.name
      item.lastChild.textContent = [city.postcode, department].filter(Boolean).join(" · ")
      item.addEventListener("click", () => this.select(index))
      return item
    }))
    cities.length ? this.open() : this.close()
  }

  keydown(event) {
    if (!this.menu.classList.contains("show")) return

    if (event.key == "ArrowDown" || event.key == "ArrowUp") {
      event.preventDefault()
      const step = event.key == "ArrowDown" ? 1 : -1
      this.highlight((this.active + step + this.cities.length) % this.cities.length)
    } else if (event.key == "Enter" && this.active >= 0) {
      event.preventDefault()
      this.select(this.active)
    } else if (event.key == "Escape") {
      this.close()
    }
  }

  highlight(index) {
    this.menu.children[this.active]?.classList.remove("active")
    this.active = index
    const item = this.menu.children[index]
    item.classList.add("active")
    item.scrollIntoView({ block: "nearest" })
    this.city.setAttribute("aria-activedescendant", item.id)
  }

  select(index) {
    const city = this.cities[index]
    this.city.value = city.name
    if (this.zipcode) {
      this.zipcode.value = city.postcode || ""
      this.zipcode.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this.close()
  }

  open() {
    this.menu.classList.add("show")
    this.city.setAttribute("aria-expanded", "true")
  }

  close() {
    this.menu?.classList.remove("show")
    this.city?.setAttribute("aria-expanded", "false")
    this.city?.removeAttribute("aria-activedescendant")
  }
}
